import Awesomplete from "awesomplete";
import pluralize from "@/utils/pluralize";
// Stable identity for a broadcast entry's value. The broadcast template's
// value is always the config string (e.g. "90"); a per-point fromBroadcast
// entry's `value` may have been coerced to a number by the metadata dialog
// (coerceValue: "90" -> 90), while `broadcastValue` stays the original "90".
// Compare the durable `broadcastValue` (falling back to `value` for legacy
// entries stamped before broadcastValue was tracked), as strings, so the
// number/string drift can't defeat the cascade's dedup and removal matching.
function sameBroadcastValue(entry, configValue) {
	return String(entry.broadcastValue ?? entry.value) === String(configValue);
}

// Full collapse identity for a fromBroadcast entry already stamped on a point:
// the (tag name, stable broadcast value, dataset scope) tuple. Used to delete
// pre-existing same-identity duplicates so points saved before the dedup fix
// collapse their repeated copies -- including number/string-drifted ones
// (value 90 vs "90", same broadcastValue "90") -- to a single entry. Shares the
// same String(broadcastValue ?? value) normalization as sameBroadcastValue, so
// drifted copies fold together while a tag legitimately broadcast with two
// different values stays distinct. Separator matches the cascade's rowKey.
function broadcastIdentityKey(entry) {
	return [
		entry.tag?.name ?? "",
		String(entry.broadcastValue ?? entry.value),
		entry.datasetUuid || "",
	].join("::");
}

/**
 * Initializes WPD alignAxes object.
 */
function init(wpd, wpdDocument, dataEntryVM) {
	const originalPickCorners = wpd.alignAxes.pickCorners;
	const awesompletes = [];

	const awesompleteSelectHandler = (event) => {
		const input = event.target
			.closest(".awesomplete")
			.querySelector(".axis-data-type");

		wpd.alignAxes.validateDataType(input);
	};

	// clear sidebar on pick corners call
	wpd.alignAxes.pickCorners = function () {
		originalPickCorners();
		wpd.sidebar.clear();
	};

	wpd.alignAxes.pickHeaderCorners = function () {
		wpd.popup.close("bar-categorical-axes-info");
		wpd.sidebar.show("bar-header-calibration-sidebar");
		wpd.graphicsWidget.setTool(
			new wpd.BarCornersTool(wpd.tree.getActiveAxes()),
		);
	};

	wpd.alignAxes.categoryChange = function (lockLines, resetTool, isEdit) {
		const headerCountField = wpdDocument.getElementById(
			isEdit ? "edit-bar-header-category-count" : "bar-header-category-count",
		);

		const errorText = headerCountField
			.closest("p")
			.querySelector(".error-message");
		if (
			isNaN(headerCountField.value) ||
			headerCountField.value < 1 ||
			headerCountField > 50
		) {
			errorText.innerText = "Must be a number greater than zero";
			return;
		}

		const headerCount = parseInt(headerCountField.value, 10);
		const activeAxes = wpd.tree.getActiveAxes();
		const metadata = activeAxes.getMetadata();
		metadata.barHeaders.count = headerCount;

		if (lockLines) {
			metadata.barHeaders.linesLocked = true;
		}

		activeAxes.setMetadata(metadata);

		if (resetTool) {
			wpd.graphicsWidget.removeTool();
			wpd.graphicsWidget.forceHandlerRepaint();
			wpd.graphicsWidget.setTool(
				new wpd.BarHeadersTool(wpd.tree.getActiveAxes(), dataEntryVM, isEdit),
			);
		}
	};

	wpd.alignAxes.completeHeaderCorners = function () {
		wpd.sidebar.clear();
		wpd.sidebar.show("bar-header-count-sidebar");
		const activeAxes = wpd.tree.getActiveAxes();
		const metadata = activeAxes.getMetadata();
		if (!metadata.barHeaders) {
			throw new Error("Bar chart corners not set");
		}

		wpd.alignAxes.categoryChange(false, true);
	};

	wpd.alignAxes.changeCategoryCount = function (isEdit) {
		wpd.alignAxes.categoryChange(false, true, isEdit);
	};

	wpd.alignAxes.completeCategoryCount = function () {
		wpd.alignAxes.categoryChange(true, false);
		wpd.graphicsWidget.forceHandlerRepaint();

		// The bar headers tool holds its own copy of the axis metadata, taken
		// before the lock was set, and writes it back on the next mouse move.
		// Redraw it so it re-reads the locked metadata and builds the headers.
		wpd.graphicsWidget.activeTool?.onRedraw?.();

		// reset category count
		const headerCountField = wpdDocument.getElementById(
			"bar-header-category-count",
		);
		headerCountField.value = "1";

		wpd.sidebar.clear();
		wpd.sidebar.show("bar-header-metadata-sidebar");
	};

	wpd.alignAxes.completeHeaderMetadata = function () {
		const activeAxes = wpd.tree.getActiveAxes();
		const metadata = activeAxes.getMetadata();
		const headers = metadata.barHeaders?.headers;
		if (!headers) return;

		const missingData = headers.find((header) => {
			if (!header.metadata) return true;
			const metadata = Object.keys(header.metadata).filter(
				(key) => !isNaN(key),
			);
			if (!metadata) return true;
			return false;
		});

		const el = wpdDocument.getElementById("bar-header-metadata-sidebar");
		const errorText = el.querySelector(".error-message");
		if (missingData) {
			errorText.innerText =
				"Missing header metadata; fill in metadata for each header and try again";
			return;
		} else {
			errorText.innerText = "";
		}

		wpd.dataSeriesManagement.showAddDataset();
	};

	// display all data after loading (for first load)
	wpd.alignAxes.renameAxes = function () {
		wpd.popup.close("rename-axes-popup");

		// check if this name already exists
		const name = wpdDocument
			.getElementById("rename-axes-name-input")
			.value.trim();
		const plotData = wpd.appData.getPlotData();
		if (plotData.getAxesNames().indexOf(name) >= 0 || name.length === 0) {
			const renameAxesPopup = wpdDocument.getElementById("rename-axes-popup");
			const heading = renameAxesPopup.getElementsByClassName("popupheading")[0];
			const axis = wpd.appData
				.getPlotData()
				.getAxesColl()
				.find((a) => a.name === name);

			const axisType = wpd.custom.identifyAxis(axis);

			let axisTypeName = "object";
			if (["xy", "bar"].includes(axisType)) {
				axisTypeName = "graph";
			} else if (axisType === "table") {
				axisTypeName = "table";
			}

			const headingText = `${heading.textContent} Error`;
			const headingTypeText = dataEntryVM.article(axisTypeName);
			wpd.messagePopup.show(
				headingText,
				`${headingTypeText} with this name already exists, please pick a different name.`,
				wpd.alignAxes.showRenameAxes,
			);

			return;
		}

		const axis = wpd.tree.getActiveAxes();

		// find xy labels and update axis and dataset names
		plotData.getAxesColl().forEach((a) => {
			if (a.name === `${axis.name} Labels`) {
				a.name = `${name} Labels`;
			}
		});

		axis.name = name;

		wpd.tree.refreshPreservingSelection();
		// removed wpd.tree.selectPath call
	};

	// update dataset creation behavior, only applies to non-image and non-table
	// axes (only graphs)
	wpd.alignAxes.postProcessAxesAdd = async function (axis) {
		// figure out graph type base name
		const graphName = {
			bar: "Bar",
			xy: "XY",
		};
		const graphType = wpd.custom.identifyAxis(axis);

		// immediately update the axis name
		axis.name = wpd.custom.makeAxisName(`${graphName[graphType]} graph`, true);

		// immediately set the active axis on the tree widget
		wpd.tree.setActiveAxis(axis);

		// dispatch axes add event
		wpd.events.dispatch("wpd.axes.add", {
			axes: axis,
		});

		// reset graph add new button
		wpdDocument
			.getElementById("graph-add-button")
			.classList.remove("pressed-button");

		const fileManager = wpd.appData.getFileManager();
		const pageManager = wpd.appData.getPageManager();

		// add axis to appropriate collections
		fileManager.addAxesToCurrentFile([axis]);

		if (wpd.appData.isMultipage()) {
			pageManager.addAxesToCurrentPage([axis]);
		}

		if (graphType === "bar") {
			// initialize dropdowns and load saved values
			wpd.utils.initializeRatioTypesSelect(
				wpdDocument.getElementById("bar-axis-change-from-baseline"),
			);

			this.initializeBarGraphFields();

			wpdDocument
				.getElementById("bar-graph-datatype-buttons")
				.removeAttribute("hidden");
			wpdDocument
				.getElementById("edit-bar-graph-datatype-buttons")
				.setAttribute("hidden", "true");
			wpd.popup.show("bar-graph-axes-label-popup");
		} else {
			if (!dataEntryVM.reworkXYGraphCalibration) {
				// initialize dropdowns and load saved values
				wpd.utils.initializeRatioTypesSelect(
					wpdDocument.getElementById("x-axis-change-from-baseline"),
				);
				wpd.utils.initializeRatioTypesSelect(
					wpdDocument.getElementById("y-axis-change-from-baseline"),
				);

				this.initializeXyGraphFields(false);
			}

			wpdDocument
				.getElementById("xy-graph-axis-buttons")
				.removeAttribute("hidden");
			wpdDocument
				.getElementById("xy-edit-graph-axis-buttons")
				.setAttribute("hidden", "true");

			if (dataEntryVM.reworkXYGraphCalibration) {
				wpd.sidebar.show("xy-graph-align-axes-sidebar");
			} else {
				wpd.popup.show("graph-axes-label-popup");
			}
		}
	};

	//TODO: remove isSidebar flag when reworkXYGraphCalibration is deprecated
	wpd.alignAxes.initializeXyGraphFields = function (isSidebar) {
		const prefix = isSidebar ? "add-" : "";
		const xAxisDataTypeInput = wpdDocument.getElementById(
			prefix + "x-axis-data-type",
		);
		const yAxisDataTypeInput = wpdDocument.getElementById(
			prefix + "y-axis-data-type",
		);

		// Suggest only tags configured for graph/table extraction, then drop
		// "Unit" and "Time (unit)" (those are applied via axesLabelData.unit
		// instead of the data-type picker).
		const tags = dataEntryVM
			.getGraphEligibleTags()
			.filter((tag) => !["Unit", "Time (unit)"].includes(tag.name));

		awesompletes[0] = new Awesomplete(xAxisDataTypeInput, {
			list: tags,
			autoFirst: true,
			data: function (item) {
				return {
					label: item.name,
					value: item.uuid,
				};
			},
			replace: function (suggestion) {
				this.input.value = suggestion.label;
			},
		});
		awesompletes[1] = new Awesomplete(yAxisDataTypeInput, {
			list: tags,
			autoFirst: true,
			data: function (item) {
				return {
					label: item.name,
					value: item.uuid,
				};
			},
			replace: function (suggestion) {
				this.input.value = suggestion.label;
			},
		});

		xAxisDataTypeInput.removeEventListener(
			"awesomplete-selectcomplete",
			awesompleteSelectHandler,
		);
		xAxisDataTypeInput.addEventListener(
			"awesomplete-selectcomplete",
			awesompleteSelectHandler,
		);
		yAxisDataTypeInput.removeEventListener(
			"awesomplete-selectcomplete",
			awesompleteSelectHandler,
		);
		yAxisDataTypeInput.addEventListener(
			"awesomplete-selectcomplete",
			awesompleteSelectHandler,
		);

		xAxisDataTypeInput.removeEventListener("blur", awesompleteSelectHandler);
		yAxisDataTypeInput.removeEventListener("blur", awesompleteSelectHandler);
		xAxisDataTypeInput.addEventListener("blur", awesompleteSelectHandler);
		yAxisDataTypeInput.addEventListener("blur", awesompleteSelectHandler);

		// attach blur handler to decimal place inputs
		wpdDocument
			.getElementById(prefix + "x-axis-decimals")
			.removeEventListener("blur", wpd.alignAxes.validateDecimals);
		wpdDocument
			.getElementById(prefix + "y-axis-decimals")
			.removeEventListener("blur", wpd.alignAxes.validateDecimals);
		wpdDocument
			.getElementById(prefix + "x-axis-decimals")
			.addEventListener("blur", wpd.alignAxes.validateDecimals);
		wpdDocument
			.getElementById(prefix + "y-axis-decimals")
			.addEventListener("blur", wpd.alignAxes.validateDecimals);

		// attach blur handler to unit inputs
		wpdDocument
			.getElementById(prefix + "x-axis-unit")
			.removeEventListener("blur", wpd.alignAxes.validateUnit);
		wpdDocument
			.getElementById(prefix + "y-axis-unit")
			.removeEventListener("blur", wpd.alignAxes.validateUnit);
		wpdDocument
			.getElementById(prefix + "x-axis-unit")
			.addEventListener("blur", wpd.alignAxes.validateUnit);
		wpdDocument
			.getElementById(prefix + "y-axis-unit")
			.addEventListener("blur", wpd.alignAxes.validateUnit);

		//TODO: should this be behind XY rework flag?
		// wpd.utils.initializeRatioTypesSelect(
		// 	wpdDocument.getElementById(prefix + "x-axis-change-from-baseline")
		// );
		// wpd.utils.initializeRatioTypesSelect(
		// 	wpdDocument.getElementById(prefix + "y-axis-change-from-baseline")
		// );
	};

	wpd.alignAxes.initializeBarGraphFields = function () {
		const barAxisDataTypeInput =
			wpdDocument.getElementById("bar-axis-data-type");
		awesompletes[0] = new Awesomplete(barAxisDataTypeInput, {
			list: dataEntryVM.getGraphEligibleTags(),
			autoFirst: true,
			data: function (item) {
				return {
					label: item.name,
					value: item.uuid,
				};
			},
			replace: function (suggestion) {
				this.input.value = suggestion.label;
			},
		});

		barAxisDataTypeInput.removeEventListener(
			"awesomplete-selectcomplete",
			awesompleteSelectHandler,
		);
		barAxisDataTypeInput.addEventListener(
			"awesomplete-selectcomplete",
			awesompleteSelectHandler,
		);

		wpdDocument
			.getElementById("bar-axis-decimals")
			.removeEventListener("blur", wpd.alignAxes.validateDecimals);
		wpdDocument
			.getElementById("bar-axis-decimals")
			.addEventListener("blur", wpd.alignAxes.validateDecimals);
	};

	// Broadcast tags: graph-level tags applied to every extracted data point.
	// Stored on metadata.axesLabelData.broadcastTags as [{ tagName, value }].

	const BROADCAST_ROWS_CONTAINER_IDS = {
		"popup-xy": "broadcast-tags-rows",
		"popup-bar": "bar-broadcast-tags-rows",
		"sidebar-xy": "add-broadcast-tags-rows",
	};

	function getBroadcastRowsContainer(isSidebar, graphType) {
		const key =
			graphType === "bar" ? "popup-bar" : isSidebar ? "sidebar-xy" : "popup-xy";
		return wpdDocument.getElementById(BROADCAST_ROWS_CONTAINER_IDS[key]);
	}

	function appendBroadcastTagRow(rowsContainer, prefill) {
		const row = wpdDocument.createElement("div");
		row.className = "broadcast-tag-row";

		const awesompleteWrapper = wpdDocument.createElement("span");
		awesompleteWrapper.className = "awesomplete";
		const tagInput = wpdDocument.createElement("input");
		tagInput.type = "text";
		tagInput.className = "broadcast-tag-name";
		tagInput.placeholder = "Tag name (e.g. Count of participants)";
		awesompleteWrapper.appendChild(tagInput);

		const valueInput = wpdDocument.createElement("input");
		valueInput.type = "text";
		valueInput.className = "broadcast-tag-value";
		valueInput.placeholder = "Optional value (blank = fill in per point)";

		const removeBtn = wpdDocument.createElement("button");
		removeBtn.type = "button";
		removeBtn.className = "broadcast-tag-remove";
		removeBtn.title = "Remove";
		removeBtn.textContent = "×";
		removeBtn.addEventListener("click", (event) => {
			event.preventDefault();
			wpd.alignAxes.removeBroadcastTagRow(removeBtn);
		});

		row.appendChild(awesompleteWrapper);
		row.appendChild(valueInput);
		row.appendChild(removeBtn);
		rowsContainer.appendChild(row);

		if (prefill) {
			tagInput.value = prefill.tagName || "";
			valueInput.value = prefill.value || "";
		}

		// Suggest only tags configured for graph/table extraction (same
		// filter as the axis-data-type pickers), so deprecated/non-graph
		// tags don't surface here. Unlike those pickers we keep "Unit" and
		// "Time (unit)" available -- they're graph-eligible, and in this
		// section curators may want to apply a single unit (or time unit)
		// to every extracted data point.
		const tags = dataEntryVM.getGraphEligibleTags();
		const awe = new Awesomplete(tagInput, {
			list: tags,
			autoFirst: true,
			data: function (item) {
				return { label: item.name, value: item.uuid };
			},
			replace: function (suggestion) {
				this.input.value = suggestion.label;
			},
		});
		awesompletes.push(awe);

		return row;
	}

	wpd.alignAxes.addBroadcastTagRow = function (linkEl, prefill) {
		const section = linkEl.closest(".broadcast-tags-section");
		if (!section) return;
		const rowsContainer = section.querySelector(".broadcast-tags-rows");
		const row = appendBroadcastTagRow(rowsContainer, prefill);
		if (!prefill) {
			row.querySelector(".broadcast-tag-name").focus();
		}
	};

	wpd.alignAxes.removeBroadcastTagRow = function (buttonEl) {
		const row = buttonEl.closest(".broadcast-tag-row");
		if (row) row.remove();
	};

	wpd.alignAxes.populateBroadcastTags = function (
		broadcastTags,
		isSidebar,
		graphType,
	) {
		const container = getBroadcastRowsContainer(isSidebar, graphType);
		if (!container) return;
		container.replaceChildren();
		if (!Array.isArray(broadcastTags)) return;
		// The Axis Data Types dialog only handles graph-wide entries; per-branch
		// entries are managed in the Graph Study Branches dialog.
		for (const tag of broadcastTags) {
			if (tag?.datasetUuid) continue;
			appendBroadcastTagRow(container, tag);
		}
	};

	wpd.alignAxes.collectBroadcastTags = function (isSidebar, graphType) {
		const container = getBroadcastRowsContainer(isSidebar, graphType);
		if (!container) return [];
		const rows = container.querySelectorAll(".broadcast-tag-row");
		const result = [];
		for (const row of rows) {
			const tagName = row.querySelector(".broadcast-tag-name").value.trim();
			const value = row.querySelector(".broadcast-tag-value").value.trim();
			// Skip rows without a tag name (treated as removed)
			if (!tagName) continue;
			result.push({ tagName, value });
		}
		return result;
	};

	// In-UI confirmation dialog for the broadcast-tags cascade. Shown via
	// wpd.popup; the OK/Cancel buttons resolve the promise returned to the
	// caller. Only one prompt is in flight at a time - showBroadcastCascadeConfirm
	// is awaited by cascadeBroadcastTagsChange before any other cascade can run.
	let broadcastCascadeConfirmResolve = null;

	wpd.alignAxes.showBroadcastCascadeConfirm = function (message) {
		const messageEl = wpdDocument.getElementById(
			"broadcast-cascade-confirm-message",
		);
		if (messageEl) messageEl.textContent = message;
		wpd.popup.show("broadcast-cascade-confirm-dialog");
		return new Promise((resolve) => {
			broadcastCascadeConfirmResolve = resolve;
		});
	};

	wpd.alignAxes.broadcastCascadeConfirmOk = function () {
		wpd.popup.close("broadcast-cascade-confirm-dialog");
		const resolve = broadcastCascadeConfirmResolve;
		broadcastCascadeConfirmResolve = null;
		if (resolve) resolve(true);
	};

	wpd.alignAxes.broadcastCascadeConfirmCancel = function () {
		wpd.popup.close("broadcast-cascade-confirm-dialog");
		const resolve = broadcastCascadeConfirmResolve;
		broadcastCascadeConfirmResolve = null;
		if (resolve) resolve(false);
	};

	// Cascades broadcast-tag changes from the calibration to existing extracted
	// points. The diff is over (tagName, hasValue) pairs - empty-value broadcast
	// rows aren't persisted on points, so they don't participate in the cascade
	// (they're injected dynamically into the per-point edit dialog instead).
	// Added entries (newly-set value) are stamped onto every point that doesn't
	// already have that tag. Removed entries (value cleared or row removed)
	// prompt the user before being stripped. Only entries flagged
	// fromBroadcast: true are touched, so manually added tag-value pairs that
	// happen to share a name are left alone.
	// Branch-aware: per-dataset effective sets resolve graph-wide vs branch
	// scoping (branch-scoped wins for the same tag). A scope or value change
	// for an existing tag is handled as a remove + add.
	wpd.alignAxes.cascadeBroadcastTagsChange = async function (
		axis,
		before,
		after,
	) {
		// Compute the effective broadcast set for a dataset: filter to entries
		// applicable to this dataset (graph-wide or matching scope), then key
		// each entry by `tagName::datasetUuid::value` so multiple rows of the
		// same tag at the same scope (or across graph + branch scopes) all
		// survive into the diff as distinct entities.
		const rowKey = (e) => `${e.tagName}::${e.datasetUuid || ""}::${e.value}`;

		const effectiveFor = (broadcastList, datasetUuid) => {
			const list = (Array.isArray(broadcastList) ? broadcastList : []).filter(
				(t) => t?.tagName && t.value,
			);
			const applicable = list.filter(
				(t) => !t.datasetUuid || t.datasetUuid === datasetUuid,
			);
			const byKey = new Map();
			for (const e of applicable) {
				byKey.set(rowKey(e), e);
			}
			return byKey;
		};

		const datasets = wpd.appData.getPlotData().getDatasetsForAxis(axis);

		// Build per-dataset plans. Identity is the
		// `tagName::datasetUuid::value` tuple, so the curator can add
		// multiple rows with the same tag name at the same or different
		// scopes and each row is treated as a distinct entity.
		//
		// `trulyRemoved` is the diff (before - after): rows the curator
		// removed from the broadcast config. These are destructive and
		// gated by a prompt.
		//
		// `toApply` is the full after-set, not the diff. Clicking OK is a
		// convergent re-apply: every configured broadcast row is stamped
		// on every eligible pixel. The per-pixel loop's hasBroadcastEntry
		// tuple-identity check skips entries already present, so this is
		// a no-op for rows that haven't changed; rows missing on a pixel
		// (e.g. because the curator locally deleted the fromBroadcast
		// entry via the per-pixel metadata dialog) get reapplied. A
		// value/scope edit on a single row surfaces as the old key
		// removed plus a new key applied.
		const plans = datasets.map((ds) => {
			const dsUuid = ds.getMetadata()?.uuid;
			const beforeMap = effectiveFor(before, dsUuid);
			const afterMap = effectiveFor(after, dsUuid);
			const trulyRemoved = [];
			for (const [key, entry] of beforeMap) {
				if (!afterMap.has(key)) trulyRemoved.push(entry);
			}
			const toApply = Array.from(afterMap.values());
			return { ds, dsUuid, trulyRemoved, toApply };
		});

		// Bail if there is nothing to remove and nothing to apply
		if (
			plans.every((p) => p.trulyRemoved.length === 0 && p.toApply.length === 0)
		) {
			return;
		}

		// Whether a fromBroadcast entry on a point matches one of the
		// `trulyRemoved` rows. Identity is the (tagName, value, datasetUuid)
		// tuple, so other entries with the same tag name but different
		// value/scope on the same point are unrelated and survive.
		const matchesRemoval = (entry, removedRows) => {
			if (!entry?.fromBroadcast) return false;
			for (const row of removedRows) {
				if (row.tagName !== entry.tag?.name) continue;
				if (
					sameBroadcastValue(entry, row.value) &&
					(row.datasetUuid || null) === (entry.datasetUuid || null)
				) {
					return true;
				}
			}
			return false;
		};

		// Aggregate for the removal prompt - only true removals trigger the
		// prompt; value/scope changes apply silently.
		let pointsAffectedByRemoval = 0;
		const allRemovedNames = new Set();
		plans.forEach((p) => {
			if (p.trulyRemoved.length === 0) return;
			p.trulyRemoved.forEach((row) => allRemovedNames.add(row.tagName));
			p.ds.getAllPixels().forEach((px) => {
				const md = px.metadata || {};
				for (const key of Object.keys(md)) {
					if (isNaN(key)) continue;
					if (matchesRemoval(md[key], p.trulyRemoved)) {
						pointsAffectedByRemoval++;
						break;
					}
				}
			});
		});

		// Prompt before removing. Cancel keeps the tag on existing points
		// (the new broadcast config still applies to future extractions).
		let removeFromPoints = true;
		if (pointsAffectedByRemoval > 0) {
			const tagsList = [...allRemovedNames].map((n) => `"${n}"`).join(", ");
			const message = `Remove ${tagsList} from ${pluralize("existing data point", pointsAffectedByRemoval, true)}?`;
			removeFromPoints = await wpd.alignAxes.showBroadcastCascadeConfirm(
				message,
			);
		}

		plans.forEach(({ ds, trulyRemoved, toApply }) => {
			ds.getAllPixels().forEach((px) => {
				const md = px.metadata;
				if (!md) return;
				let dirty = false;

				// Apply true removals (gated by the user prompt). Only
				// fromBroadcast entries matching the removed (tagName, value,
				// datasetUuid) tuple are deleted, so unrelated rows of the
				// same tag survive.
				if (removeFromPoints && trulyRemoved.length > 0) {
					for (const key of Object.keys(md)) {
						if (isNaN(key)) continue;
						if (matchesRemoval(md[key], trulyRemoved)) {
							delete md[key];
							dirty = true;
						}
					}
				}

				// Collapse pre-existing same-identity fromBroadcast duplicates
				// to one, keeping the first (lowest-index, earliest-stamped)
				// copy. Points saved before the dedup fix can carry the same
				// broadcast several times, including number/string-drifted
				// copies that broadcastIdentityKey normalizes together. Runs
				// after removals and before nextIdx/hasBroadcastEntry so the
				// apply loop and index operate on the collapsed set; the
				// trailing reindexMetadata re-sequences keys after the deletes.
				const seenBroadcastIds = new Set();
				for (const key of Object.keys(md)) {
					if (isNaN(key)) continue;
					const entry = md[key];
					if (!entry?.fromBroadcast) continue;
					const id = broadcastIdentityKey(entry);
					if (seenBroadcastIds.has(id)) {
						delete md[key];
						dirty = true;
					} else {
						seenBroadcastIds.add(id);
					}
				}

				const numericKeys = Object.keys(md)
					.filter((k) => !isNaN(k))
					.map(Number);
				let nextIdx =
					numericKeys.length === 0 ? 0 : Math.max(...numericKeys) + 1;

				for (const broadcast of toApply) {
					const tag = wpd.tagGroups.findTag({ name: broadcast.tagName });
					if (!tag) continue;
					// Skip if an identical fromBroadcast entry already exists.
					// Identity is (tagName, value, datasetUuid) -- other rows of
					// the same tag with different values or scopes don't
					// suppress this one. Broadcasts coexist with curator-typed
					// entries (no manual-wins-over-broadcast suppression).
					const hasBroadcastEntry = Object.keys(md).some((k) => {
						if (isNaN(k)) return false;
						const e = md[k];
						if (!e?.fromBroadcast || e.tag?.name !== tag.name) return false;
						return (
							sameBroadcastValue(e, broadcast.value) &&
							(e.datasetUuid || null) === (broadcast.datasetUuid || null)
						);
					});
					if (hasBroadcastEntry) continue;
					md[nextIdx++] = {
						tag: { name: tag.name, uuid: tag.uuid },
						value: broadcast.value,
						fromBroadcast: true,
						broadcastValue: broadcast.value,
						...(broadcast.datasetUuid
							? { datasetUuid: broadcast.datasetUuid }
							: {}),
					};
					dirty = true;
				}

				if (dirty) {
					wpd.utils.reindexMetadata(md);
				}
			});
			wpd.custom.syncMetadataKeys(ds);
			wpd.custom.repaintDataPoints(ds);
		});
	};

	wpd.alignAxes.validateDataType = function (input) {
		const value = input.value.trim();
		const isEmpty = value === undefined || value === null || value === "";
		const errorText = input
			.closest(".axis-data-type-input")
			.querySelector(".error-message");
		const tagNames = dataEntryVM.getTags().map((tag) => tag.name);

		if (isEmpty) {
			errorText.innerText = "Data type cannot be empty";
			input.classList.add("input-error");
			return false;
		} else if (!tagNames.includes(value)) {
			errorText.innerText = "Data type must be a value tag name";
			input.classList.add("input-error");
			return false;
		}

		errorText.innerText = "";
		input.classList.remove("input-error");

		return true;
	};

	wpd.alignAxes.validateUnit = function ({ target }) {
		const value = target.value?.trim();
		const isEmpty = value === undefined || value === null || value === "";
		const errorText = target
			.closest(".axis-data-type-input")
			.querySelector(".error-message");

		if (isEmpty) {
			errorText.innerText = "Unit cannot be empty";
			target.classList.add("input-error");
			return false;
		}

		errorText.innerText = "";
		target.classList.remove("input-error");

		return true;
	};

	wpd.alignAxes.validateDecimals = function ({ target }) {
		if (!target.value) {
			// default to 2 if empty
			target.value = "2";
		}
	};

	//TODO: remove isSidebar flag when reworkXYGraphCalibration is deprecated
	wpd.alignAxes.validateAll = function (isSidebar) {
		const axis = wpd.tree.getActiveAxes();
		const graphType = wpd.custom.identifyAxis(axis);

		const prefix = isSidebar ? "add-" : "";
		if (graphType === "bar") {
			const barAxisDataType = wpdDocument.getElementById("bar-axis-data-type");
			const barAxisDataTypeValid =
				wpd.alignAxes.validateDataType(barAxisDataType);
			const barAxisUnit = wpdDocument.getElementById("bar-axis-unit");
			const barAxisUnitValid = wpd.alignAxes.validateUnit({
				target: barAxisUnit,
			});
			if (!barAxisDataTypeValid || !barAxisUnitValid) return false;
		} else {
			const xAxisDataType = wpdDocument.getElementById(
				prefix + "x-axis-data-type",
			);
			const xAxisDataTypeValid = wpd.alignAxes.validateDataType(xAxisDataType);

			const yAxisDataType = wpdDocument.getElementById(
				prefix + "y-axis-data-type",
			);
			const yAxisDataTypeValid = wpd.alignAxes.validateDataType(yAxisDataType);

			const xAxisUnit = wpdDocument.getElementById(prefix + "x-axis-unit");
			const xAxisUnitValid = wpd.alignAxes.validateUnit({
				target: xAxisUnit,
			});

			const yAxisUnit = wpdDocument.getElementById(prefix + "y-axis-unit");
			const yAxisUnitValid = wpd.alignAxes.validateUnit({
				target: yAxisUnit,
			});

			if (
				!xAxisDataTypeValid ||
				!yAxisDataTypeValid ||
				!xAxisUnitValid ||
				!yAxisUnitValid
			)
				return false;
		}

		return true;
	};

	wpd.alignAxes.destroyAwesompletes = function () {
		for (let i = 0; i < awesompletes.length; i++) {
			awesompletes[i].destroy();
		}

		awesompletes.length = 0;
	};

	wpd.alignAxes.clearAxesLabelValues = function () {
		wpdDocument.getElementById("x-axis-data-type").value = "";
		wpdDocument.getElementById("x-axis-unit").value = "";
		wpdDocument.getElementById("x-axis-decimals").value = "2";
		wpdDocument.getElementById("x-axis-change-from-baseline").value = "";

		wpdDocument.getElementById("y-axis-data-type").value = "";
		wpdDocument.getElementById("y-axis-unit").value = "";
		wpdDocument.getElementById("y-axis-decimals").value = "2";
		wpdDocument.getElementById("y-axis-change-from-baseline").value = "";

		wpdDocument.getElementById("bar-axis-data-type").value = "";
		wpdDocument.getElementById("bar-axis-unit").value = "";
		wpdDocument.getElementById("bar-axis-decimals").value = "2";
		wpdDocument.getElementById("bar-axis-change-from-baseline").value = "";

		wpdDocument.getElementById("x-axis-description").value = "";
		wpdDocument.getElementById("y-axis-description").value = "";
		wpdDocument.getElementById("bar-axis-description").value = "";

		// Clear broadcast tag rows in all three dialog variants. The
		// awesompletes attached to the rows' tag-name inputs are torn down
		// separately by destroyAwesompletes.
		for (const id of Object.values(BROADCAST_ROWS_CONTAINER_IDS)) {
			const container = wpdDocument.getElementById(id);
			if (container) container.replaceChildren();
		}
	};

	wpd.alignAxes.closeAxesLabelPopup = function () {
		const axis = wpd.tree.getActiveAxes();
		const graphType = wpd.custom.identifyAxis(axis);
		if (graphType === "bar") {
			wpd.popup.close("bar-graph-axes-label-popup");
		} else {
			wpd.popup.close("graph-axes-label-popup");
		}

		wpd.pointGroups.resetSettings();
		wpd.alignAxes.clearAxesLabelValues();
		wpd.alignAxes.destroyAwesompletes();
		wpd.alignAxes.cancelGraphAdd();
	};

	// delete newly created axis because user bailed before calibration finished
	wpd.alignAxes.cancelGraphAdd = function () {
		// locally store active axis
		const axis = wpd.tree.getActiveAxes();

		// clear active axis
		wpd.tree.setActiveAxis(null);

		// exit wizard
		wpd.tree.exitWizard();

		// delete active axis from plot data
		wpd.appData.getPlotData().deleteAxes(axis);

		// delete active axis from file manager
		const fileManager = wpd.appData.getFileManager();
		fileManager.deleteAxesFromCurrentFile([axis]);

		// delete active axis from page manager
		if (wpd.appData.isMultipage()) {
			wpd.appData.getPageManager().deleteAxesFromCurrentPage([axis]);
		}
	};

	//TODO: remove isSidebar flag when reworkXYGraphCalibration is deprecated
	wpd.alignAxes.saveAxesData = function (
		isSidebar,
		proceedToAddDatasets,
		isEdit,
	) {
		const validationResult = wpd.alignAxes.validateAll(isSidebar);
		if (!validationResult) return;
		const axis = wpd.tree.getActiveAxes();
		const metadata = axis.getMetadata();
		const graphType = wpd.custom.identifyAxis(axis);
		const prefix = isSidebar ? "add-" : "";
		const broadcastTagsBefore = metadata.axesLabelData?.broadcastTags || [];
		let values = {};
		if (graphType === "bar") {
			values.barAxisDataType =
				wpdDocument.getElementById("bar-axis-data-type").value;
			values.barAxisUnit = wpdDocument.getElementById("bar-axis-unit").value;
			values.barAxisDescription = wpdDocument.getElementById(
				"bar-axis-description",
			).value;
			values.barAxisDecimals =
				wpdDocument.getElementById("bar-axis-decimals").value;
			const barRatioEl = wpdDocument.getElementById(
				"bar-axis-change-from-baseline",
			);
			values.barAxisChangeFromBaseline =
				barRatioEl._getRatioTypeValue?.() ?? barRatioEl.value;

			const graphWideAfter = wpd.alignAxes.collectBroadcastTags(false, "bar");
			// Preserve per-branch entries (managed in the Graph Study Branches
			// dialog, not this one). The cascade compares the full before/after
			// lists; per-branch entries appear in both and are no-ops.
			const preservedBranchScoped = (broadcastTagsBefore || []).filter(
				(t) => t?.datasetUuid,
			);
			const broadcastTagsAfter = [...preservedBranchScoped, ...graphWideAfter];
			values.broadcastTagsBefore = broadcastTagsBefore;
			values.broadcastTagsAfter = broadcastTagsAfter;

			metadata.axesLabelData = {
				bar: {
					dataType: values.barAxisDataType,
					unit: values.barAxisUnit,
					description: values.barAxisDescription,
					decimals: values.barAxisDecimals,
					changeFromBaseline: values.barAxisChangeFromBaseline,
				},
				broadcastTags: broadcastTagsAfter,
			};

			if (!isEdit) {
				metadata.initialRotation = wpd.graphicsWidget.getRotation();
			}

			axis.setMetadata(metadata);
			wpd.pointGroups.saveSettings(axis);

			wpd.tree.setActiveAxis(axis);

			wpd.popup.close("bar-graph-axes-label-popup");
			wpd.alignAxes.clearAxesLabelValues();
			wpd.alignAxes.destroyAwesompletes();
			wpd.tree.refreshPreservingSelection();

			if (proceedToAddDatasets) {
				wpd.popup.show("bar-categorical-axes-info");
			}
		} else {
			values.xAxisDataType = wpdDocument.getElementById(
				prefix + "x-axis-data-type",
			).value;
			values.xAxisUnit = wpdDocument.getElementById(
				prefix + "x-axis-unit",
			).value;
			values.xAxisDescription = wpdDocument.getElementById(
				prefix + "x-axis-description",
			).value;
			values.xAxisDecimals = wpdDocument.getElementById(
				prefix + "x-axis-decimals",
			).value;
			const xRatioEl = wpdDocument.getElementById(
				prefix + "x-axis-change-from-baseline",
			);
			values.xAxisChangeFromBaseline =
				xRatioEl._getRatioTypeValue?.() ?? xRatioEl.value;

			values.yAxisDataType = wpdDocument.getElementById(
				prefix + "y-axis-data-type",
			).value;
			values.yAxisUnit = wpdDocument.getElementById(
				prefix + "y-axis-unit",
			).value;
			values.yAxisDescription = wpdDocument.getElementById(
				prefix + "y-axis-description",
			).value;
			values.yAxisDecimals = wpdDocument.getElementById(
				prefix + "y-axis-decimals",
			).value;
			const yRatioEl = wpdDocument.getElementById(
				prefix + "y-axis-change-from-baseline",
			);
			values.yAxisChangeFromBaseline =
				yRatioEl._getRatioTypeValue?.() ?? yRatioEl.value;

			const graphWideAfter = wpd.alignAxes.collectBroadcastTags(
				isSidebar,
				"xy",
			);
			// Preserve per-branch entries (managed in the Graph Study Branches
			// dialog, not this one). The cascade compares the full before/after
			// lists; per-branch entries appear in both and are no-ops.
			const preservedBranchScoped = (broadcastTagsBefore || []).filter(
				(t) => t?.datasetUuid,
			);
			const broadcastTagsAfter = [...preservedBranchScoped, ...graphWideAfter];
			values.broadcastTagsBefore = broadcastTagsBefore;
			values.broadcastTagsAfter = broadcastTagsAfter;

			metadata.axesLabelData = {
				x: {
					dataType: values.xAxisDataType,
					unit: values.xAxisUnit,
					description: values.xAxisDescription,
					decimals: values.xAxisDecimals,
					changeFromBaseline: values.xAxisChangeFromBaseline,
				},
				y: {
					dataType: values.yAxisDataType,
					unit: values.yAxisUnit,
					description: values.yAxisDescription,
					decimals: values.yAxisDecimals,
					changeFromBaseline: values.yAxisChangeFromBaseline,
				},
				broadcastTags: broadcastTagsAfter,
			};

			if (!isEdit) {
				metadata.initialRotation = wpd.graphicsWidget.getRotation();
			}

			axis.setMetadata(metadata);
			wpd.pointGroups.saveSettings(axis);

			wpd.tree.setActiveAxis(axis);

			if (!isSidebar) {
				wpd.popup.close("graph-axes-label-popup");
			}

			wpd.alignAxes.clearAxesLabelValues();
			wpd.alignAxes.destroyAwesompletes();
			wpd.tree.refreshPreservingSelection();

			if (proceedToAddDatasets) {
				wpd.dataSeriesManagement.showAddDataset();
			}
		}

		return values;
	};

	wpd.alignAxes.getAxisCorners = function (axis) {
		const axisType = wpd.custom.identifyAxis(axis);

		let topLeft, bottomRight;

		if (axisType === "xy") {
			const corners = [
				axis.calibration.getPoint(0),
				axis.calibration.getPoint(1),
				axis.calibration.getPoint(2),
				axis.calibration.getPoint(3),
			];

			topLeft = corners.reduce(
				(min, current) => ({
					px: Math.min(min.px, current.px),
					py: Math.min(min.py, current.py),
				}),
				{ px: Infinity, py: Infinity },
			);
			bottomRight = corners.reduce(
				(max, current) => ({
					px: Math.max(max.px, current.px),
					py: Math.max(max.py, current.py),
				}),
				{ px: -Infinity, py: -Infinity },
			);
		} else if (axisType === "bar") {
			const metadata = axis.getMetadata();

			// get all 4 corners
			const corners = [
				axis.calibration.getPoint(0),
				axis.calibration.getPoint(1),
				metadata.barHeaders.p1,
				metadata.barHeaders.p2,
			];

			// find min(x, y) and max(x, y)
			topLeft = corners.reduce(
				(min, current) => ({
					px: Math.min(min.px, current.px),
					py: Math.min(min.py, current.py),
				}),
				{ px: Infinity, py: Infinity },
			);
			bottomRight = corners.reduce(
				(max, current) => ({
					px: Math.max(max.px, current.px),
					py: Math.max(max.py, current.py),
				}),
				{ px: -Infinity, py: -Infinity },
			);
		}

		return { topLeft, bottomRight };
	};

	wpd.alignAxes.getGraphAxisTags = function (axis) {
		const axesLabelData = axis.getMetadata()?.axesLabelData;

		if (axesLabelData) {
			// identify the axis
			const axisType = wpd.custom.identifyAxis(axis);

			// get all tags
			const tags = dataEntryVM.getTags();

			const dataTypeTags = {};

			if (axisType === "xy") {
				const xDataType = axesLabelData.x.dataType;
				const yDataType = axesLabelData.y.dataType;

				// find the tags for the main data types
				for (const tag of tags) {
					if (xDataType && tag.name === xDataType) {
						dataTypeTags.xMainTag = tag;
					}
					if (yDataType && tag.name === yDataType) {
						dataTypeTags.yMainTag = tag;
					}

					if (dataTypeTags.xMainTag && dataTypeTags.yMainTag) {
						break;
					}
				}
			} else if (axisType === "bar") {
				const barDataType = axesLabelData.bar.dataType;

				dataTypeTags.barMainTag = tags.find((tag) => tag.name === barDataType);
			}

			return dataTypeTags;
		}
	};

	// override align function for xy axis to close popup before calling postProcessAxesAdd
	wpd.XYAxesCalibrator.prototype.align = function () {
		let xmin = wpdDocument.getElementById("xmin").value;
		let xmax = wpdDocument.getElementById("xmax").value;
		let ymin = wpdDocument.getElementById("ymin").value;
		let ymax = wpdDocument.getElementById("ymax").value;
		let xlog = wpdDocument.getElementById("xlog").checked;
		let ylog = wpdDocument.getElementById("ylog").checked;
		let noRotation = wpdDocument.getElementById("xy-axes-no-rotation").checked;

		let axes = this._isEditing ? wpd.tree.getActiveAxes() : new wpd.XYAxes();

		// validate log scale values
		if (
			(xlog && (parseFloat(xmin) <= 0 || parseFloat(xmax) <= 0)) ||
			(ylog && (parseFloat(ymin) <= 0 || parseFloat(ymax) <= 0))
		) {
			wpd.popup.close("xyAlignment");
			wpd.messagePopup.show(
				wpd.gettext("calibration-invalid-log-inputs"),
				wpd.gettext("calibration-enter-valid-log"),
				wpd.alignAxes.getCornerValues,
			);
			return false;
		}

		this._calibration.setDataAt(0, xmin, ymin);
		this._calibration.setDataAt(1, xmax, ymin);
		this._calibration.setDataAt(2, xmin, ymin);
		this._calibration.setDataAt(3, xmax, ymax);
		if (!axes.calibrate(this._calibration, xlog, ylog, noRotation)) {
			wpd.popup.close("xyAlignment");
			wpd.messagePopup.show(
				wpd.gettext("calibration-invalid-inputs"),
				wpd.gettext("calibration-enter-valid"),
				wpd.alignAxes.getCornerValues,
			);
			return false;
		}

		// the alignemnt popup should close before post processing, since it may open another popup
		wpd.popup.close("xyAlignment");

		if (!this._isEditing) {
			axes.name = wpd.alignAxes.makeAxesName(wpd.XYAxes);
			let plot = wpd.appData.getPlotData();
			plot.addAxes(axes, wpd.appData.isMultipage());
			wpd.alignAxes.postProcessAxesAdd(axes);
		}

		return true;
	};

	// override align function for bar axis to close popup before calling postProcessAxesAdd
	wpd.BarAxesCalibrator.prototype.align = function () {
		let p1 = wpdDocument.getElementById("bar-axes-p1").value;
		let p2 = wpdDocument.getElementById("bar-axes-p2").value;
		let isLogScale = wpdDocument.getElementById("bar-axes-log-scale").checked;
		let isRotated = wpdDocument.getElementById("bar-axes-rotated").checked;
		let axes = this._isEditing ? wpd.tree.getActiveAxes() : new wpd.BarAxes();

		if (isLogScale && (parseFloat(p1) <= 0 || parseFloat(p2) <= 0)) {
			wpd.popup.close("barAlignment");
			wpd.messagePopup.show(
				wpd.gettext("calibration-invalid-log-inputs"),
				wpd.gettext("calibration-enter-valid-log"),
				wpd.alignAxes.getCornerValues,
			);
			return false;
		}

		this._calibration.setDataAt(0, 0, p1);
		this._calibration.setDataAt(1, 0, p2);
		if (!axes.calibrate(this._calibration, isLogScale, isRotated)) {
			wpd.popup.close("barAlignment");
			wpd.messagePopup.show(
				wpd.gettext("calibration-invalid-inputs"),
				wpd.gettext("calibration-enter-valid"),
				wpd.alignAxes.getCornerValues,
			);
			return false;
		}

		// the alignemnt popup should close before post processing, since it may open another popup
		wpd.popup.close("barAlignment");

		if (!this._isEditing) {
			axes.name = wpd.alignAxes.makeAxesName(wpd.BarAxes);
			let plot = wpd.appData.getPlotData();
			plot.addAxes(axes, wpd.appData.isMultipage());
			wpd.alignAxes.postProcessAxesAdd(axes);
		}

		return true;
	};

	// override align function for polar axis to close popup before calling postProcessAxesAdd
	wpd.PolarAxesCalibrator.prototype.align = function () {
		let r1 = parseFloat(wpdDocument.getElementById("polar-r1").value);
		let theta1 = parseFloat(wpdDocument.getElementById("polar-theta1").value);
		let r2 = parseFloat(wpdDocument.getElementById("polar-r2").value);
		let theta2 = parseFloat(wpdDocument.getElementById("polar-theta2").value);
		let degrees = wpdDocument.getElementById("polar-degrees").checked;
		let orientation = wpdDocument.getElementById("polar-clockwise").checked;
		let rlog = wpdDocument.getElementById("polar-log-scale").checked;
		let axes = this._isEditing ? wpd.tree.getActiveAxes() : new wpd.PolarAxes();
		let isDegrees = degrees;

		this._calibration.setDataAt(1, r1, theta1);
		this._calibration.setDataAt(2, r2, theta2);
		axes.calibrate(this._calibration, isDegrees, orientation, rlog);

		// the alignemnt popup should close before post processing, since it may open another popup
		wpd.popup.close("polarAlignment");

		if (!this._isEditing) {
			axes.name = wpd.alignAxes.makeAxesName(wpd.PolarAxes);
			let plot = wpd.appData.getPlotData();
			plot.addAxes(axes, wpd.appData.isMultipage());
			wpd.alignAxes.postProcessAxesAdd(axes);
		}

		return true;
	};

	// override align function for ternary axis to close popup before calling postProcessAxesAdd
	wpd.TernaryAxesCalibrator.prototype.align = function () {
		let range100 = wpdDocument.getElementById("range0to100").checked;
		let ternaryNormal = wpdDocument.getElementById("ternarynormal").checked;
		let axes = this._isEditing
			? wpd.tree.getActiveAxes()
			: new wpd.TernaryAxes();

		axes.calibrate(this._calibration, range100, ternaryNormal);

		// the alignemnt popup should close before post processing, since it may open another popup
		wpd.popup.close("ternaryAlignment");

		if (!this._isEditing) {
			axes.name = wpd.alignAxes.makeAxesName(wpd.TernaryAxes);
			let plot = wpd.appData.getPlotData();
			plot.addAxes(axes, wpd.appData.isMultipage());
			wpd.alignAxes.postProcessAxesAdd(axes);
		}
		return true;
	};

	// override align function for map axis to close popup before calling postProcessAxesAdd
	wpd.MapAxesCalibrator.prototype.align = function () {
		let scaleLength = parseFloat(
			wpdDocument.getElementById("scaleLength").value,
		);
		let scaleUnits = wpdDocument.getElementById("scaleUnits").value;
		let isOriginLocationBottomLeft = wpdDocument.getElementById(
			"map-origin-bottom-left",
		).checked;
		let originLocation = isOriginLocationBottomLeft
			? "bottom-left"
			: "top-left";
		let imageHeight = wpd.graphicsWidget.getImageSize().height;
		let axes = this._isEditing ? wpd.tree.getActiveAxes() : new wpd.MapAxes();

		axes.calibrate(
			this._calibration,
			scaleLength,
			scaleUnits,
			originLocation,
			imageHeight,
		);

		// the alignemnt popup should close before post processing, since it may open another popup
		wpd.popup.close("mapAlignment");

		if (!this._isEditing) {
			axes.name = wpd.alignAxes.makeAxesName(wpd.MapAxes);
			let plot = wpd.appData.getPlotData();
			plot.addAxes(axes, wpd.appData.isMultipage());
			wpd.alignAxes.postProcessAxesAdd(axes);
		}

		return true;
	};

	// override align function for circular chart recorder axis to close popup before calling postProcessAxesAdd
	wpd.CircularChartRecorderCalibrator.prototype.align = function () {
		let t0 = wpdDocument.getElementById("circular-t0").value;
		let r0 = parseFloat(wpdDocument.getElementById("circular-r0").value);
		let r2 = parseFloat(wpdDocument.getElementById("circular-r2").value);
		let tstart = wpdDocument.getElementById("circular-tstart").value;
		let axes = this._isEditing
			? wpd.tree.getActiveAxes()
			: new wpd.CircularChartRecorderAxes();

		this._calibration.setDataAt(0, t0, r0);
		this._calibration.setDataAt(1, t0, 0);
		this._calibration.setDataAt(2, t0, r2);
		this._calibration.setDataAt(3, 0, r2);
		this._calibration.setDataAt(4, 0, r2);

		axes.calibrate(this._calibration, tstart);

		// the alignemnt popup should close before post processing, since it may open another popup
		wpd.popup.close("circularChartRecorderAlignment");

		if (!this._isEditing) {
			axes.name = wpd.alignAxes.makeAxesName(wpd.CircularChartRecorderAxes);
			let plot = wpd.appData.getPlotData();
			plot.addAxes(axes, wpd.appData.isMultipage());
			wpd.alignAxes.postProcessAxesAdd(axes);
		}

		return true;
	};
}

export { init, sameBroadcastValue, broadcastIdentityKey };
